import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { DateTime as dt } from 'luxon';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar';

import Fetchable from '~/commons/components/util/Fetchable';
import { resolvePath } from '~/commons/helpers';
import env from '~/commons/config/env';

import Page from '../../layout/Page';
import Scroll from '../../layout/Scroll';
import { getLevel } from '../../helpers';
import Level from '../../mods/Level';
import StageIcon from '../../mods/StageIcon';

const PageResult = () => {
  const { stage: url, difficulty, refCode } = useParams();
  const { t, i18n } = useTranslation();

  return (
    <Page>
      <Fetchable
        req={[
          { url: '/api/levels' },
          { url: '/api/users/self' },
          {
            url: '/api/stages/u',
            params: { url: `/${url}`, difficulty, refCode },
          },
        ]}
      >
        {([{ rows: levels }, user, stage]) => {
          const { answers: [answer] } = stage;
          const start = dt.fromISO(answer.createdAt);
          const end = dt.fromISO(answer.completedAt);
          const diff = end.diff(start).toFormat('mm:ss');
          const level = getLevel(levels, user.score);

          // Has the user reached a new level?
          let lvUp = user.score - answer.score < level.current;

          // if this is the final level
          if (!level.next) lvUp = true;

          // level to displayed
          const lv = lvUp ? level.current : level.next;

          return (
            <div className="container text-center">
              <Scroll fillScreen>
                {/* icon */}
                <div className="result-stage-icon">
                  <div>
                    <StageIcon stage={stage} />
                  </div>
                </div>

                {/* stats */}
                <div className="row mb-4 gy-4">
                  <div className="col-12 col-lg">
                    <div className="row gy-3 align-items-center">
                      <div className="col-12 col-sm">
                        <h4 className="fw-bold m-0">
                          {t('pg.game.resultScore')}
                          :
                        </h4>
                      </div>
                      <div className="col fw-bold fs-5">
                        <div className="border border-2 border-primary rounded-pill p-3 px-2">
                          {t('total')}
                          :
                          {' '}
                          {answer.score}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg">
                    <div className="row gy-3 align-items-center">
                      <div className="col-12 col-sm">
                        <h4 className="fw-bold m-0">
                          {t('pg.game.resultDuration')}
                          :
                        </h4>
                      </div>
                      <div className="col fw-bold fs-5">
                        <div className="border border-2 border-primary rounded-pill p-3 px-2">
                          {t('total')}
                          :
                          {' '}
                          {diff}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* level up */}
                <div className="row justify-content-center">
                  <div className="col-12 col-md-8">
                    <div className="row justify-content-center align-items-center gy-4">
                      <Level
                        className="col-auto"
                        level={lv}
                        current={lv}
                        scored={user.score}
                        progress
                      />
                      <div className="col-12 col-md">
                        <div
                          className={`
                            rounded-pill p-3 px-2 mt-3
                            text-white text-center
                            ${lvUp ? 'bg-primary' : 'bg-secondary'}
                          `}
                        >
                          <h3 className="m-0">
                            <FA icon={faStar} />
                            {' '}
                            {t(`pg.game.resultLv${lvUp ? '' : 'Not'}Up`)}
                            {' '}
                            <FA icon={faStar} />
                          </h3>
                        </div>
                        <h2 className="mt-4 fw-bold">
                          {t(
                            `pg.game.resultLv${lvUp ? '' : 'Not'}UpDesc`,
                            { level: i18n.pickLng((lv.title)) },
                          )}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </Scroll>
              <div className="text-center answersBtnGrp position-relative">
                <Link
                  className="btn mx-3 btn-rock-3 fontBorder"
                  to={resolvePath('..')}
                >
                  {t('pg.game.tryAgain')}
                </Link>
                <Link
                  className="btn mx-3 btn-rock-3 fontBorder"
                  to={resolvePath(env.lowMode ? '/game' : '../..')}
                >
                  {t('pg.game.quit')}
                </Link>
              </div>
            </div>
          );
        }}
      </Fetchable>
    </Page>
  );
};

export default PageResult;
